import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth, errorMessage } from '../../context/AuthContext';
import SubjectPicker from '../../components/SubjectPicker';

// Un sujet : modifier son nom, le supprimer, gérer ses QCM
export default function AdminQcmSubject() {
  const { id } = useParams();
  const { authFetch } = useAuth();
  const navigate = useNavigate();
  const [subject, setSubject] = useState(null);
  const [form, setForm] = useState({ name: '', description: '' });
  const [flash, setFlash] = useState(null);
  const [editing, setEditing] = useState(false);
  const [all, setAll] = useState([]);
  useEffect(() => {
    authFetch('/api/admin/qcm/subjects').then(r => r.ok ? r.json() : []).then(l => setAll(l.map(s => ({ id: s.id, name: s.name, count: s.quiz_count })))).catch(() => {});
    // eslint-disable-next-line
  }, [id]);

  const load = () => authFetch(`/api/admin/qcm/subjects/${id}`)
    .then(async r => { if (!r.ok) throw new Error(await errorMessage(r)); return r.json(); })
    .then(d => { setSubject(d); setForm({ name: d.name, description: d.description || '' }); })
    .catch(e => setFlash({ type: 'error', text: `Erreur : ${e.message}` }));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const saveSubject = async (e) => {
    e.preventDefault();
    try {
      const r = await authFetch(`/api/admin/qcm/subjects/${id}`, { method: 'PUT', body: JSON.stringify(form) });
      if (!r.ok) throw new Error(await errorMessage(r));
      setEditing(false);
      setFlash({ type: 'ok', text: '✓ sujet enregistré' });
      setTimeout(() => setFlash(null), 2000);
      load();
    } catch (err) {
      setFlash({ type: 'error', text: `Non enregistré : ${err.message}` });
    }
  };

  const deleteSubject = async () => {
    const n = subject.quizzes.length;
    const msg = n
      ? `Supprimer le sujet « ${subject.name} » ET ses ${n} QCM ? Cette action est définitive.`
      : `Supprimer le sujet « ${subject.name} » ?`;
    if (!window.confirm(msg)) return;
    try {
      const r = await authFetch(`/api/admin/qcm/subjects/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(await errorMessage(r));
      navigate('/admin/qcm');
    } catch (err) {
      setFlash({ type: 'error', text: `Suppression impossible : ${err.message}` });
    }
  };

  const deleteQuiz = async (q) => {
    if (!window.confirm(`Supprimer le QCM « ${q.title} » et ses ${q.question_count} question(s) ?`)) return;
    try {
      const r = await authFetch(`/api/admin/qcm/quizzes/${q.id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(await errorMessage(r));
      load();
    } catch (err) {
      setFlash({ type: 'error', text: `Suppression impossible : ${err.message}` });
    }
  };

  const togglePublish = async (q) => {
    try {
      const r = await authFetch(`/api/admin/qcm/quizzes/${q.id}/publish`, { method: 'PATCH', body: JSON.stringify({ published: !q.published }) });
      if (!r.ok) throw new Error(await errorMessage(r));
      setFlash({ type: 'ok', text: q.published ? `« ${q.title} » est repassé en brouillon` : `✓ « ${q.title} » est maintenant visible sur le site` });
      setTimeout(() => setFlash(null), 2500);
      load();
    } catch (err) {
      setFlash({ type: 'error', text: `Impossible : ${err.message}` });
    }
  };

  if (!subject) return <div>{flash ? <div className="flash error">{flash.text}</div> : 'chargement…'}</div>;

  return (
    <div>
      <Link to="/admin/qcm" className="qcm-back">← tous les sujets</Link>
      <SubjectPicker subjects={all} current={subject.id} allHref="/admin/qcm" hrefFor={sid => `/admin/qcm/sujets/${sid}`} unit="QCM" />

      {flash && <div className={`flash ${flash.type === 'error' ? 'error' : ''}`}>{flash.text}</div>}

      {!editing ? (
        <div className="qcm-subject-head">
          <div>
            <h1 style={{ fontStyle: 'italic' }}>{subject.name}</h1>
            {subject.description && <p style={{ color: 'var(--ink-soft)', marginTop: '.4rem' }}>{subject.description}</p>}
          </div>
          <div className="admin-row-actions">
            <button className="btn secondary" onClick={() => setEditing(true)}>renommer</button>
            <button className="btn danger" onClick={deleteSubject}>supprimer le sujet</button>
          </div>
        </div>
      ) : (
        <form onSubmit={saveSubject} className="qcm-panel" style={{ marginBottom: '2rem' }}>
          <div className="qcm-grid-2">
            <div className="form-group">
              <label>Nom du sujet</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required autoFocus />
            </div>
            <div className="form-group">
              <label>Description</label>
              <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>
          <button className="btn" type="submit">enregistrer</button>
          <button className="btn secondary" type="button" style={{ marginLeft: '.5rem' }}
            onClick={() => { setEditing(false); setForm({ name: subject.name, description: subject.description || '' }); }}>annuler</button>
        </form>
      )}

      <div className="qcm-actions-bar">
        <Link to={`/admin/qcm/nouveau?sujet=${subject.id}`} className="btn">+ nouveau QCM</Link>
        <Link to={`/admin/qcm/sujets/${subject.id}/import`} className="btn secondary">⇪ importer un QCM (CSV)</Link>
      </div>

      <div className="admin-list">
        {subject.quizzes.length === 0 && <div className="empty-state">aucun QCM dans ce sujet.</div>}
        {subject.quizzes.map(q => (
          <div className="admin-row" key={q.id}>
            <div>
              <div className="admin-row-title">{q.title}</div>
              <div style={{ fontSize: '.8rem', color: 'var(--ink-soft)', fontFamily: 'var(--font-mono)' }}>
                {q.question_count} question(s) · {q.timer_enabled ? `⏱ ${q.timer_minutes} min` : 'sans timer'} · correction {q.feedback_mode === 'immediate' ? 'immédiate' : 'à la fin'}{' '}
                <span className={`qcm-status ${q.published ? 'on' : ''}`}>{q.published ? '● visible sur le site' : '○ brouillon (invisible)'}</span>
              </div>
            </div>
            <div className="admin-row-actions">
              <button className={`btn ${q.published ? 'secondary' : ''}`} onClick={() => togglePublish(q)}>{q.published ? 'dépublier' : 'publier'}</button>
              {q.published && <a href={`/qcm/${q.id}`} target="_blank" rel="noreferrer" className="btn secondary">voir</a>}
              <Link to={`/admin/qcm/${q.id}`} className="btn secondary">modifier</Link>
              <button className="btn danger" onClick={() => deleteQuiz(q)}>supprimer</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
